// src/pages/SafetyTipsPage.jsx
// Do's & Don'ts — cracker safety guide.
// Mobile: segmented toggle between Do's / Don'ts. Desktop: side by side.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './InfoPages.css';

const DOS = [
  { icon: '🏪', t: 'Buy from a trusted seller', d: 'Purchase fireworks only from licensed, trusted shops.' },
  { icon: '📖', t: 'Read the instructions', d: 'Read the label on each cracker and follow the directions before lighting.' },
  { icon: '👨‍👧', t: 'Adult supervision', d: 'Children should burst crackers only in the presence of an adult.' },
  { icon: '🌳', t: 'Use open spaces', d: 'Light crackers in a wide open area, away from houses and dry materials.' },
  { icon: '🪣', t: 'Keep water & sand ready', d: 'Keep two buckets of water and sand nearby to put out fire quickly.' },
  { icon: '👕', t: 'Wear cotton clothes', d: 'Wear close-fitting cotton clothes and footwear while bursting crackers.' },
  { icon: '🕯️', t: 'Use a long agarbatti', d: 'Light crackers with a long incense stick or sparkler, keeping your arm fully stretched.' },
  { icon: '📏', t: 'Keep a safe distance', d: 'Move away quickly after lighting, and keep spectators at a safe distance.' },
  { icon: '📦', t: 'Store safely', d: 'Keep crackers in a closed box, away from lamps, candles and the kitchen.' },
  { icon: '💧', t: 'Soak used crackers', d: 'Put used sparklers and burnt crackers in a bucket of water before throwing away.' },
  { icon: '🐶', t: 'Care for pets', d: 'Keep pets indoors in a quiet room — loud sounds frighten them.' },
  { icon: '🩹', t: 'Keep a first-aid kit', d: 'Have a first-aid box ready for minor burns and injuries.' },
];

const DONTS = [
  { icon: '✋', t: "Don't hold in your hand", d: 'Never light a cracker while holding it in your hand.' },
  { icon: '🔁', t: "Don't relight duds", d: "If a cracker doesn't burst, don't go near it or try to relight it. Pour water over it." },
  { icon: '🧥', t: "Don't wear loose / synthetic clothes", d: 'Synthetic, silk and loose flowing clothes catch fire easily.' },
  { icon: '🏠', t: "Don't burst indoors", d: 'Never light crackers inside the house, in narrow lanes or in closed spaces.' },
  { icon: '🚗', t: "Don't burst near vehicles", d: 'Keep away from parked vehicles, petrol pumps and gas cylinders.' },
  { icon: '🏥', t: "Don't disturb others", d: 'Avoid bursting crackers near hospitals, schools and elderly people.' },
  { icon: '👖', t: "Don't carry in pockets", d: 'Never keep crackers in your pockets or clothes.' },
  { icon: '🎯', t: "Don't throw at anyone", d: 'Never throw crackers at people, animals or vehicles — even for fun.' },
  { icon: '🚀', t: "Don't aim rockets badly", d: "Don't launch rockets near buildings, trees, windows or overhead wires." },
  { icon: '🧪', t: "Don't experiment", d: "Don't open, mix or make homemade crackers. Never combine two crackers." },
  { icon: '🧒', t: "Don't leave kids alone", d: 'Never let children handle or light crackers without an adult.' },
  { icon: '⏱️', t: "Don't ignore local rules", d: 'Follow the timings and rules set by your local authorities.' },
];

const FIRST_AID = [
  { icon: '🔥', t: 'For burns', d: 'Cool the burn under clean running water for 10–20 minutes. Do not apply ice, toothpaste or oil. See a doctor for serious burns.' },
  { icon: '👁️', t: 'For eye injury', d: "Do not rub the eye. Gently rinse with clean water and go to a doctor immediately." },
  { icon: '🧯', t: 'Clothes catch fire', d: 'Stop, drop and roll. Cover the person with a thick cotton blanket to put out the flames.' },
];

const HELPLINES = [
  ['101', 'Fire'], ['108', 'Ambulance'], ['112', 'Emergency'],
];

function Card({ item, kind, i }) {
  return (
    <motion.li className={`sd-card sd-${kind}`}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(i * .035, .35), duration: .35 }}>
      <span className="sd-icon" aria-hidden="true">{item.icon}</span>
      <div>
        <h3 className="sd-title">{item.t}</h3>
        <p className="sd-text">{item.d}</p>
      </div>
      <span className="sd-mark" aria-hidden="true">{kind === 'do' ? '✓' : '✕'}</span>
    </motion.li>
  );
}

export default function SafetyTipsPage() {
  const [tab, setTab] = useState('do');

  return (
    <div className="page-top ip-page">
      <section className="ip-hero ip-hero-safety">
        <div className="ip-hero-glow" aria-hidden="true" />
        <div className="ip-wrap ip-hero-inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}>
            <span className="ip-eyebrow">🧯 Celebrate safely</span>
            <h1 className="ip-title">Do's &amp; <span>Don'ts</span></h1>
            <p className="ip-lead">A few simple habits keep your family safe and your Diwali happy.</p>
          </motion.div>
        </div>
      </section>

      <div className="ip-wrap">
        {/* Mobile toggle */}
        <div className="sd-toggle" role="tablist" aria-label="Show do's or don'ts">
          <button role="tab" aria-selected={tab === 'do'} className={tab === 'do' ? 'is-on do' : ''}
            onClick={() => setTab('do')}>✓ Do's <small>{DOS.length}</small></button>
          <button role="tab" aria-selected={tab === 'dont'} className={tab === 'dont' ? 'is-on dont' : ''}
            onClick={() => setTab('dont')}>✕ Don'ts <small>{DONTS.length}</small></button>
          <span className={`sd-toggle-pill ${tab}`} aria-hidden="true" />
        </div>

        <div className="sd-grid">
          <section className={`sd-col sd-col-do ${tab === 'do' ? 'is-active' : ''}`}>
            <h2 className="sd-col-head do"><span>✓</span> Do's</h2>
            <ul className="sd-list" key={'do' + tab}>
              {DOS.map((it, i) => <Card key={it.t} item={it} kind="do" i={i} />)}
            </ul>
          </section>

          <section className={`sd-col sd-col-dont ${tab === 'dont' ? 'is-active' : ''}`}>
            <h2 className="sd-col-head dont"><span>✕</span> Don'ts</h2>
            <ul className="sd-list" key={'dont' + tab}>
              {DONTS.map((it, i) => <Card key={it.t} item={it} kind="dont" i={i} />)}
            </ul>
          </section>
        </div>

        {/* First aid */}
        <section className="sd-aid">
          <h2 className="sd-aid-title">🩹 Quick first aid</h2>
          <div className="sd-aid-grid">
            {FIRST_AID.map(a => (
              <div key={a.t} className="sd-aid-card">
                <span className="sd-aid-icon" aria-hidden="true">{a.icon}</span>
                <h3>{a.t}</h3>
                <p>{a.d}</p>
              </div>
            ))}
          </div>

          <div className="sd-help">
            <p>In an emergency, call</p>
            <div className="sd-help-nums">
              {HELPLINES.map(([n, l]) => (
                <a key={n} href={`tel:${n}`} className="sd-help-num">
                  <b>{n}</b><span>{l}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <div className="sd-cta">
          <Link to="/products" className="ip-btn ip-btn-fire">🎆 Shop crackers</Link>
          <Link to="/terms" className="ip-btn ip-btn-outline">📜 Terms &amp; Conditions</Link>
        </div>
      </div>
    </div>
  );
}
